import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, MapPin, X } from 'lucide-react';
import type { Project } from '../data/projects';
type LightboxProps = {
  project: Project;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
};
export function Lightbox({
  project,
  onClose,
  onPrev,
  onNext
}: LightboxProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onPrev();
      if (event.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} role="dialog" aria-modal="true" aria-label={`${project.type} in ${project.location}`} className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/95 p-4 backdrop-blur-sm sm:p-8" onClick={onClose} data-id="element-224" data-mp="22ujp2p6x6bjz">
      <button type="button" onClick={onClose} aria-label="Close gallery" className="absolute right-4 top-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10 sm:right-8 sm:top-8" data-id="element-225" data-mp="1uct7tup593yi">
        <X className="h-5 w-5" aria-hidden="true" data-id="element-226" data-mp="2i2f68fxozf5g" />
      </button>

      <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.98
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} onClick={event => event.stopPropagation()} className="grid w-full max-w-5xl overflow-hidden rounded-2xl bg-navy-900 lg:grid-cols-[1.5fr_1fr]" data-id="element-227" data-mp="2dziiybx7iuv6">
        <img src={project.image} alt={`${project.type} in ${project.location}`} className="max-h-[45vh] w-full object-cover lg:max-h-[75vh]" data-id="element-228" data-mp="z2oa5ebe4s6y" />
        <div className="flex flex-col justify-center p-7 sm:p-10" data-id="element-229" data-mp="13zofto4bvsxj">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brass-light" data-id="element-230" data-mp="q17y724xgi0m">
            {project.type}
          </p>
          <p className="mt-3 flex items-center gap-2 text-sm text-white/60" data-id="element-231" data-mp="u5o2x02t1je9">
            <MapPin className="h-4 w-4 text-brass" aria-hidden="true" data-id="element-232" data-mp="galr5wba0ztj" />
            {project.location}
          </p>
          <p className="mt-5 text-[15px] leading-relaxed text-white/75" data-id="element-233" data-mp="l4zo435l7f0b">
            {project.description}
          </p>
          <ul className="mt-6 flex flex-wrap gap-2" data-id="element-234" data-mp="bd3f3x1yq4hb">
            {project.services.map(service => <li key={service} className="rounded-full border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] text-white/60" data-id="element-235" data-mp="3tiyjb2kqiwcr">
                {service}
              </li>)}
          </ul>
          <div className="mt-8 flex gap-3" data-id="element-236" data-mp="3ew9imxeqyd4s">
            <button type="button" onClick={onPrev} aria-label="Previous project" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10" data-id="element-237" data-mp="39xg554zgmm4m">
              <ChevronLeft className="h-5 w-5" aria-hidden="true" data-id="element-238" data-mp="1oy2pyy1j94v" />
            </button>
            <button type="button" onClick={onNext} aria-label="Next project" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10" data-id="element-239" data-mp="3sw286pejenb6">
              <ChevronRight className="h-5 w-5" aria-hidden="true" data-id="element-240" data-mp="kfz1alv912nw" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}